import { getWalletClient } from 'services/wallet/config'
import { EmptyProviderError } from 'utils/errors'
import {
  WalletClient,
  encodeFunctionData,
  hexToNumber,
  pad,
  slice,
  toHex,
  type Address,
  type Hex,
  type TypedDataDomain,
} from 'viem'
import { multicall, readContract, signTypedData } from 'viem/actions'
import { ChainId } from './interfaces'
import { PermitTypes } from 'indices-list'

export type PermitSignature = {
  r: Hex
  s: Hex
  v: number
}

export type PermitSign = PermitSignature & { deadline: bigint }
export type PermitSignStore = PermitSign & { address: Address; amount: Big }

export const getPermitData = async (
  account: Address,
  spender: Address,
  value: bigint,
  permit?: PermitSignStore,
) => {
  if (permit === undefined) {
    return '0x'
  }
  return encodeFunctionData({
    abi: erc20PermitAbi,
    functionName: 'permit',
    args: [account, spender, value, permit.deadline, permit.v, permit.r, permit.s],
  })
}

export const getPermitParams = async (
  account: Address,
  chainId: ChainId,
  token: Address,
  spender: Address,
  value: bigint,
  permit: PermitTypes,
): Promise<PermitSign> => {
  if (permit === undefined) {
    return {
      deadline: 0n,
      r: pad('0x', { size: 32 }),
      s: pad('0x', { size: 32 }),
      v: 0,
    }
  }
  const client = getWalletClient(chainId)
  if (client === null) throw new EmptyProviderError()

  const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 60) // 1 hour
  const contract = {
    address: token,
    abi: erc20PermitAbi,
  }

  const [multicallRes, versionRes] = await Promise.allSettled([
    multicall(client, {
      contracts: [
        {
          ...contract,
          functionName: 'nonces',
          args: [account],
        },
        {
          ...contract,
          functionName: 'name',
        },
      ],
      allowFailure: false,
    }),
    readContract(client, {
      ...contract,
      functionName: 'version',
    }),
  ])

  if (multicallRes.status === 'rejected') {
    throw multicallRes.reason
  }

  const [nonce, name] = multicallRes.value

  const version = versionRes.status === 'rejected' ? '1' : versionRes.value
  const isDai = permit === 'dai'

  const data = isDai
    ? await signPermitDai(client, {
        chainId,
        nonce,
        deadline,
        name,
        owner: account,
        spender,
        verifyingContract: token,
      })
    : await signPermit(client, {
        chainId,
        spender,
        value,
        nonce,
        deadline,
        verifyingContract: token,
        name,
        owner: account,
        version,
      })

  return {
    deadline,
    ...data,
  }
}

const erc20PermitAbi = [
  {
    inputs: [],
    stateMutability: 'view',
    type: 'function',
    name: 'name',
    outputs: [
      {
        internalType: 'string',
        name: '',
        type: 'string',
      },
    ],
  },
  {
    type: 'function',
    inputs: [
      { name: 'owner', internalType: 'address', type: 'address' },
      { name: 'spender', internalType: 'address', type: 'address' },
      { name: 'value', internalType: 'uint256', type: 'uint256' },
      { name: 'deadline', internalType: 'uint256', type: 'uint256' },
      { name: 'v', internalType: 'uint8', type: 'uint8' },
      { name: 'r', internalType: 'bytes32', type: 'bytes32' },
      { name: 's', internalType: 'bytes32', type: 'bytes32' },
    ],
    name: 'permit',
    outputs: [],
    stateMutability: 'nonpayable',
  },
  {
    inputs: [],
    stateMutability: 'view',
    type: 'function',
    name: 'version',
    outputs: [
      {
        internalType: 'string',
        name: '',
        type: 'string',
      },
    ],
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'owner',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
    name: 'nonces',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
  },
] as const

type SignPermitProps = {
  /** Address of the token to approve */
  verifyingContract: Hex
  /** Name of the token to approve.
   * Corresponds to the `name` method on the ERC-20 contract. Please note this must match exactly byte-for-byte */
  name: string
  /** Owner of the tokens. Usually the currently connected address. */
  owner: Hex
  /** Address to grant allowance to */
  spender: Hex
  /** Expiration of this approval, in SECONDS */
  deadline: bigint
  /** Numerical chainId of the token contract */
  chainId: number
  /** Nonce of the approval */
  nonce: bigint
  /** Version */
  version?: string
}

type Eip2612Props = SignPermitProps & {
  /** Amount to approve */
  value: bigint
}

const signPermit = async (
  account: WalletClient,
  {
    verifyingContract,
    name,
    owner,
    spender,
    value,
    deadline,
    nonce,
    chainId,
    version,
  }: Eip2612Props,
): Promise<PermitSignature> => {
  const actualVersion = version ?? '1'

  const types = {
    Permit: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'deadline', type: 'uint256' },
    ],
  }

  const domain = {
    name,
    version: actualVersion,
    chainId,
    verifyingContract,
  }

  const message = {
    owner,
    spender,
    value,
    nonce,
    deadline,
  }

  // ON FE USE THIS:
  const signature = await signTypedData(account, {
    account: owner,
    message,
    domain,
    primaryType: 'Permit',
    types,
  })
  const [r, s, v] = [slice(signature, 0, 32), slice(signature, 32, 64), slice(signature, 64, 65)]
  return { r, s, v: hexToNumber(v) }
}

const signPermitDai = async (
  account: WalletClient,
  { verifyingContract, name, owner, spender, deadline, nonce, chainId }: SignPermitProps,
): Promise<PermitSignature> => {
  const types = {
    Permit: [
      { name: 'holder', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'nonce', type: 'uint256' },
      { name: 'expiry', type: 'uint256' },
      { name: 'allowed', type: 'bool' },
    ],
  }

  let domain: TypedDataDomain = {
    name,
    /** There are no known Dai deployments with Dai permit and version other than or unspecified */
    version: '1',
    chainId,
    verifyingContract,
  }
  /** USDC on Polygon is a special case */
  if (chainId === 137 && name === 'USD Coin (PoS)') {
    domain = {
      name,
      version: '1',
      verifyingContract,
      salt: pad(toHex(137), { size: 32 }),
    }
  }

  const message = {
    holder: owner,
    spender,
    nonce,
    expiry: deadline,
    /** true == infinite allowance, false == 0 allowance*/
    allowed: true,
  }

  // ON FE USE THIS:
  const signature = await signTypedData(account, {
    account: owner,
    domain,
    primaryType: 'Permit',
    types,
    message,
  })
  const [r, s, v] = [slice(signature, 0, 32), slice(signature, 32, 64), slice(signature, 64, 65)]
  return { r, s, v: hexToNumber(v) }
}
